import React, { useEffect, useState, useCallback, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Controller } from 'react-hook-form';
import { getAsyncDictionary } from 'requests/dictionary';
import { EDictionaryTypes } from 'models/dictionary';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { StyledFormElementWrapper } from 'theme/styles';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { useTranslations } from 'hooks/useTranslations';
import { useDebounce } from 'hooks/useDebounce';
import axios from 'axios';
import { StyledAutocomplete, StyledInnerWrapper, StyledTextField } from './styles';
import { isEmpty } from 'utils/search';

interface IFormAsyncAutocompleteProps extends IFormElementProps {
  dictionaryType: EDictionaryTypes;
  name: string;
  control: any;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  freeSolo?: boolean;
}

const LIMIT = 25;

export const FormAsyncAutocomplete = ({
  dictionaryType,
  name,
  control,
  label,
  disabled,
  required,
  errors,
  className,
  freeSolo,
}: IFormAsyncAutocompleteProps) => {
  const [options, setOptions] = useState<IFormElementOptionProp[]>([]);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const debouncedQuery = useDebounce(query, 300);

  const { t } = useTranslations();

  const loadOptions = useCallback(
    async (pageToLoad: number, reset: boolean, search: string) => {
      const requestId = ++requestIdRef.current;

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const data = await getAsyncDictionary(
          dictionaryType,
          pageToLoad,
          LIMIT,
          search,
          controller.signal,
        );

        if (!data || requestId !== requestIdRef.current) return;

        const mapped = data.content.map((item) => ({
          label: item.name,
          value: item.uniqueId,
        }));

        setTotalElements(data.totalElements);

        if (reset) setOptions(mapped);
        else setOptions((prev) => [...prev, ...mapped]);
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [dictionaryType],
  );

  useEffect(() => {
    setPage(0);
    loadOptions(0, true, debouncedQuery);
  }, [debouncedQuery, loadOptions]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = listboxNode;

    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (isNearBottom && !loading && options.length < totalElements) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(nextPage, false, debouncedQuery);
    }
  };

  return (
    <StyledFormElementWrapper className={className}>
      <FormLabel name={name} label={label} disabled={disabled} required={required} />
      <StyledInnerWrapper>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selectedOption =
              options.find((opt) => opt.value === field.value) ??
              (field.value ? { label: field.value, value: field.value } : null);
            return (
              <StyledAutocomplete
                id={name}
                forcePopupIcon={false}
                popupIcon={null}
                clearIcon={<Icon type={EIconTypes.clear} />}
                options={options}
                getOptionLabel={(option: any) => option?.label ?? ''}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value?.value
                }
                loading={loading}
                disabled={disabled}
                freeSolo={freeSolo}
                value={selectedOption}
                onChange={(_, value: any) => {
                  if (typeof value === 'string') {
                    field.onChange(value);
                  } else {
                    field.onChange(value?.value ?? '');
                  }
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') setQuery(value);
                  else if (reason === 'clear') setQuery('');
                }}
                clearOnBlur
                onBlur={(event: any) => {
                  const inputValue = event.target.value;
                  if (!isEmpty(inputValue) && isEmpty(field.value)) {
                    setQuery('');
                  }
                  const currentLabel = selectedOption?.label ?? '';
                  if (
                    !isEmpty(inputValue) &&
                    !isEmpty(field.value) &&
                    inputValue !== currentLabel
                  ) {
                    setQuery(currentLabel);
                  }
                }}
                slotProps={{
                  listbox: { onScroll: handleScroll },
                }}
                renderInput={(params) => (
                  <StyledTextField
                    ref={field.ref}
                    {...params}
                    error={!!errors[name]}
                    placeholder={t('formAutoComplete.all.placeholder')}
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        endAdornment: (
                          <>
                            {loading && <CircularProgress size={20} />}
                            {params.slotProps.input.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            );
          }}
        />
      </StyledInnerWrapper>
      <FormErrorLabel label={errors[name]?.message} />
    </StyledFormElementWrapper>
  );
};
